<template>
  <div class="p-6 max-w-6xl mx-auto space-y-6">
    <el-card shadow="hover">
      <template #header>
        <div class="flex items-start md:items-center justify-between gap-3 flex-col md:flex-row">
          <h2 class="text-lg font-semibold">{{ isMobile() ? '年金计算' : '年金计算（PV / FV / PMT / n / r）' }}</h2>
          <el-segmented class="mobile-wrap w-full sm:w-auto" v-model="unknown" :options="unknownOptions" />
        </div>
      </template>

      <el-alert type="info" :closable="false" class="mb-3" title="用途：在年金模型中互相求解 PV、FV、PMT、n、r，常用于理财、储蓄、分期等周期性现金流的估算。" />
      <FormulaPanel title="普通年金（期末支付）" :formulas="[pvFormula, fvFormula]"
        :variables="[{ symbol: 'PMT', label: '每期支付' }, { symbol: 'r', label: '每期利率' }, { symbol: 'n', label: '期数' }, { symbol: 'PV', label: '现值' }, { symbol: 'FV', label: '未来值' }]"
        :notes="['本页采用普通年金，支付发生在期末']" />
      <div class="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
        <el-form label-position="top" class="md:col-span-4 grid grid-cols-1 md:grid-cols-5 gap-4">
          <el-form-item :label="`每期支付（${symbol}）`" v-if="unknown !== 'pmt'">
            <el-input v-model.number="pmtUI" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写PMT）</div>
          </el-form-item>
          <el-form-item label="每期利率(%)" v-if="unknown !== 'r'">
            <el-input v-model.number="form.rPct" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写r）</div>
          </el-form-item>
          <el-form-item label="期数" v-if="unknown !== 'n'">
            <el-input v-model.number="form.n" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写n）</div>
          </el-form-item>
          <el-form-item :label="`现值（${symbol}）`" v-if="unknown !== 'pv'">
            <el-input v-model.number="pvUI" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写PV）</div>
          </el-form-item>
          <el-form-item :label="`未来值（${symbol}）`" v-if="unknown !== 'fv'">
            <el-input v-model.number="fvUI" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写FV）</div>
          </el-form-item>
        </el-form>
        <div class="md:col-span-1 flex gap-3 md:justify-end flex-wrap">
          <el-button type="primary" @click="onSolve">求解</el-button>
          <el-button @click="onReset">重置</el-button>
        </div>
      </div>

      <el-alert v-if="error" type="error" class="mt-4" :closable="false" :title="error" />

      <el-collapse-transition>
        <div v-if="solved" class="mt-4">
          <el-descriptions title="求解结果（普通年金，期末支付）" :column="3" border>
            <el-descriptions-item :label="`PMT（${symbol}）`">{{ d2(solved.pmt) }}</el-descriptions-item>
            <el-descriptions-item label="r(%)">{{ solved.rPct }}</el-descriptions-item>
            <el-descriptions-item label="n">{{ solved.n }}</el-descriptions-item>
            <el-descriptions-item :label="`PV（${symbol}）`">{{ d2(solved.pv) }}</el-descriptions-item>
            <el-descriptions-item :label="`FV（${symbol}）`">{{ d2(solved.fv) }}</el-descriptions-item>
          </el-descriptions>
        </div>
      </el-collapse-transition>
    </el-card>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { round, pvAnnuity, fvAnnuity, pmtFromPV, pmtFromFV, nFromPV, nFromFV, rateFromPV, rateFromFV } from '@/lib/tvmEngine'
import FormulaPanel from '@/components/ui/FormulaPanel.vue'
import { useCurrency } from '@/composables/useCurrency'
function isMobile() {
  return /Android|webOS|iPhone|iPod|iPad|BlackBerry|Mobile/i.test(navigator.userAgent)
}
const pvFormula = String.raw`PV = PMT \cdot \dfrac{1 - (1+r)^{-n}}{r}`
const fvFormula = String.raw`FV = PMT \cdot \dfrac{(1+r)^n - 1}{r}`

const unknownOptions = [
  { label: '未知 PMT', value: 'pmt' },
  { label: '未知 r', value: 'r' },
  { label: '未知 n', value: 'n' },
  { label: '未知 PV', value: 'pv' },
  { label: '未知 FV', value: 'fv' }
]

const unknown = ref('pv')
const form = ref({ pmt: 1000, rPct: 1, n: 12, pv: null, fv: null })
// 货币换算
const { symbol, toDisplay, toBase } = useCurrency()
const pmtUI = computed({ get: () => toDisplay(form.value.pmt), set: v => form.value.pmt = toBase(v) })
const pvUI = computed({ get: () => toDisplay(form.value.pv), set: v => form.value.pv = toBase(v) })
const fvUI = computed({ get: () => toDisplay(form.value.fv), set: v => form.value.fv = toBase(v) })
const solved = ref(null)
const error = ref('')

const onSolve = () => {
  error.value = ''
  solved.value = null
  try {
    let { pmt, rPct, n, pv, fv } = form.value
    const r = Number(rPct) / 100

    if (unknown.value === 'pv') {
      if (!(pmt > 0 && n > 0 && rPct >= 0)) throw new Error('此处填写 PMT / r / n')
      pv = round(pvAnnuity(pmt, r, n))
      fv = round(fvAnnuity(pmt, r, n))
    } else if (unknown.value === 'fv') {
      if (!(pmt > 0 && n > 0 && rPct >= 0)) throw new Error('此处填写 PMT / r / n')
      fv = round(fvAnnuity(pmt, r, n))
      pv = round(pvAnnuity(pmt, r, n))
    } else if (unknown.value === 'pmt') {
      if (pv != null && pv > 0) {
        if (!(rPct >= 0 && n > 0)) throw new Error('此处填写 PV / r / n')
        pmt = round(pmtFromPV(pv, r, n))
        fv = round(fvAnnuity(pmt, r, n))
      } else if (fv != null && fv > 0) {
        if (!(rPct >= 0 && n > 0)) throw new Error('此处填写 FV / r / n')
        pmt = round(pmtFromFV(fv, r, n))
        pv = round(pvAnnuity(pmt, r, n))
      } else {
        throw new Error('此处填写 PV 或 FV')
      }
    } else if (unknown.value === 'n') {
      if (pv != null && pv > 0) {
        if (!(pmt > 0 && rPct >= 0)) throw new Error('此处填写 PMT / r / PV')
        n = Math.ceil(nFromPV(pv, r, pmt))
        fv = round(fvAnnuity(pmt, r, n))
      } else if (fv != null && fv > 0) {
        if (!(pmt > 0 && rPct >= 0)) throw new Error('此处填写 PMT / r / FV')
        n = Math.ceil(nFromFV(fv, r, pmt))
        pv = round(pvAnnuity(pmt, r, n))
      } else {
        throw new Error('此处填写 PV 或 FV')
      }
    } else if (unknown.value === 'r') {
      if (pv != null && pv > 0) {
        if (!(pmt > 0 && n > 0)) throw new Error('此处填写 PMT / n / PV')
        const rp = rateFromPV(pv, n, pmt)
        rPct = round(rp * 100, 4)
        fv = round(fvAnnuity(pmt, rp, n))
      } else if (fv != null && fv > 0) {
        if (!(pmt > 0 && n > 0)) throw new Error('此处填写 PMT / n / FV')
        const rp = rateFromFV(fv, n, pmt)
        rPct = round(rp * 100, 4)
        pv = round(pvAnnuity(pmt, rp, n))
      } else {
        throw new Error('此处填写 PV 或 FV')
      }
    }

    solved.value = { pmt, rPct, n, pv, fv }
  } catch (e) {
    error.value = e?.message || '求解失败'
  }
}

const onReset = () => {
  unknown.value = 'pv'
  form.value = { pmt: 0, rPct: 0, n: 0, pv: null, fv: null }
  solved.value = null
  error.value = ''
}

// 显示用：金额两位小数
const d2 = (v) => Number(toDisplay(v)).toFixed(2)

</script>

<style scoped>
.mobile-wrap :deep(.el-segmented__group) {
  flex-wrap: wrap;
  row-gap: 6px;
}
</style>
